import React, { useEffect, useState, useMemo } from 'react';
import classNames from 'classnames';
import { ResponsiveImage } from '../..';
import { FormattedMessage } from '../../../util/reactIntl';
import { Modal, Button, InlineTextButton } from '../../../components';

import css from './PartialCancelModal.css';

const getPreviousDay = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  date.setDate(date.getDate() - 1);

  // Formatowanie z powrotem do formatu YYYY-MM-DD (lub takiego, jaki potrzebujesz)
  return date.toISOString().split('T')[0];
};

const PartialCancelModal = props => {
  const {
    className,
    rootClassName,
    id,
    isOpen,
    onCloseModal,
    onManageDisableScrolling,
    partialCancelInProgress,
    partialCancelError,
    partialCancelSuccess,
    onPartialCancelOrder,
    onCancelSale,
    transaction,
    actualOrderedProducts,
    cancellationByCustomer = false,
    cancelInProgress
  } = props;
  const { listings, provider } = transaction;
  const isHungarianProvider = provider.attributes.countryId === 'hu';

  const classes = classNames(rootClassName || css.root, className);

  const productsWithActualSeats = useMemo(() => {
    return actualOrderedProducts.reduce((accumulator, { listingId, seats }) => {
      accumulator[listingId] = (accumulator[listingId] || 0) + seats;
      return accumulator;
    }, {});
  }, [actualOrderedProducts]);

  const [productSeats, setProductSeats] = useState(productsWithActualSeats);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isConfirmQuestion, setIsConfirmQuestion] = useState(false);
  const [isError, setIsError] = useState(false);

  const handleChange = event => {
    setProductSeats(prevState => ({
      ...prevState,
      [event.target.name]: event.target.value,
    }));
  };

  const handleClose = () => {
    setProductSeats(productsWithActualSeats);
    setIsConfirmQuestion(false);
    onCloseModal();
    setIsError(false);
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (isConfirmQuestion) {
      const items = [];
      let isZeroQuantity = true;
      for (let key in productSeats) {
        if (productSeats[key] > 0) isZeroQuantity = false;
        const { seats } = actualOrderedProducts.find(({ listingId }) => listingId === key);
        const amount = seats - productSeats[key];
        amount && items.push({ listingId: key, amount });
      }
      isZeroQuantity ? await onCancelSale() : await onPartialCancelOrder(transaction.id.uuid, items);
      onCloseModal();
    } else {
      setIsConfirmQuestion(true);
    }
  };

  const compareSeats = (productSeats, actualOrderedProducts) => {
    let isNotChanged = true;

    actualOrderedProducts.forEach(({ listingId, seats }) => {
      if (seats !== productSeats[listingId]) isNotChanged = false;
    });

    return isNotChanged;
  };

  const checkSeatsOfProduct = productSeats => {
    let isZeroQuantity = true;

    for (let key in productSeats) {
      if (productSeats[key] > 0) isZeroQuantity = false;
    }

    return isZeroQuantity;
  };

  useEffect(() => {
    const condition = (!cancellationByCustomer && checkSeatsOfProduct(productSeats)) || compareSeats(productSeats, actualOrderedProducts);
    setIsDisabled(condition);
  }, [productSeats, actualOrderedProducts]);

  useEffect(() => {
    partialCancelSuccess && handleClose();
    partialCancelError && setIsError(true);
  }, [partialCancelSuccess, partialCancelError]);

  const questionConfirmClass = isError ? css.error : css.confirmQuestion;

  const selectNumber = itemNumber => {
    const arrayItems = [];
    for (let i = itemNumber; i >= 0; i--) {
      arrayItems.push(i);
    }

    return arrayItems.map(item => <option key={item} value={item}>{item}</option>);
  };

  let partialCancelMsgId = "PartialCancelModal.confirmQuestion";
  let cancelBtnMessageId = "PartialCancelModal.cancel";
  let confirmBtnMessageId = "PartialCancelModal.confirm";

  let quantityMessageId = "PartialCancelModal.quantity";
  let labelMessageId = "PartialCancelModal.label";

  if (isHungarianProvider && cancellationByCustomer) {
    quantityMessageId = "PartialCancelModal.quantity.hungarianCustomer";
    labelMessageId = "PartialCancelModal.label.hungarianCustomer";
    partialCancelMsgId = "PartialCancelModal.confirmQuestion.hungarianCustomer"

    if (!isConfirmQuestion) {
      cancelBtnMessageId = "PartialCancelModal.btn.megse";
      confirmBtnMessageId = "PartialCancelModal.btn.megerosites";
    } else {
      cancelBtnMessageId = "PartialCancelModal.btn.vissza";
      confirmBtnMessageId = "PartialCancelModal.btn.elallasMegerositese";
    }
  }

  if (!isHungarianProvider && cancellationByCustomer) {
    partialCancelMsgId = "PartialCancelModal.confirmationQuestionCustomer"
  }

  return (
    <Modal
      id={id}
      containerClassName={classes}
      isOpen={isOpen}
      onClose={handleClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
      closeButtonMessage={' '}
    >
      <div className={css.modal}>
        <div className={css.modalContent}>
          <p className={css.modalTitle}>
            <FormattedMessage id={cancellationByCustomer ? "PartialCancelModal.customerTitle" : "PartialCancelModal.title"} />
          </p>
          {
            isHungarianProvider && cancellationByCustomer && !isConfirmQuestion &&
            <div className={css.customerData}>
              <div>{`${transaction.user.attributes.firstName} ${transaction.user.attributes.lastName}`}</div>
              <div className={css.emailAddress}>{transaction.user.attributes.email}</div>
              <div>{`${transaction.attributes.start} - ${getPreviousDay(transaction.attributes.end)}`}</div>
            </div>
          }
          {isConfirmQuestion ? (
            <p className={classNames(css.text, questionConfirmClass)}>
              <FormattedMessage
                id={isError ? 'PartialCancelModal.error' : partialCancelMsgId}
                values={{
                  br: () => <br />
                }}
              />
            </p>
          ) : (
            actualOrderedProducts.map(({ listingId, seats }) => {
              const listing = listings.find(({ id: { uuid } }) => uuid === listingId);
              const { uuid } = listing.id;
              const image = listing?.images[0];
              const { title } = listing.attributes;
              return productsWithActualSeats[listingId] ? (
                <div className={css.productContainer} key={uuid}>
                  {image && (
                    <ResponsiveImage
                      rootClassName={css.rootForImage}
                      image={image}
                      alt={'product'}
                      variants={['square-small', 'square-small2x']}
                    />
                  )}
                  <div className={css.productContent}>
                    <p className={css.productName}>{title.toLowerCase()}</p>
                    <div className={css.productDetails}>
                      <p className={css.text}>
                        <FormattedMessage id={quantityMessageId} /> <b>{seats}</b>
                      </p>
                      <div>
                        <label className={css.label}>
                          <FormattedMessage id={labelMessageId} />
                        </label>
                        <select
                          value={productSeats[listingId]}
                          name={listingId}
                          onChange={handleChange}
                        >
                          {selectNumber(productsWithActualSeats[listingId])}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null;
            })
          )}
        </div>
        {
          isHungarianProvider && cancellationByCustomer && !isConfirmQuestion &&
          <div>
            <div className={css.statement}>
              <FormattedMessage id="PartialCancelModal.statement.shortenPeriod" />
            </div>
            <div className={css.statement}>
              <FormattedMessage id="PartialCancelModal.statement.declaration" />
            </div>
          </div>
        }
        <div className={css.actionSection}>
          <InlineTextButton className={css.cancelBtn} onClick={handleClose}>
            <FormattedMessage id={cancelBtnMessageId} />
          </InlineTextButton>
          <Button
            className={css.saveBtn}
            inProgress={partialCancelInProgress || cancelInProgress}
            onClick={handleSubmit}
            disabled={isDisabled}
          >
            <FormattedMessage id={confirmBtnMessageId} />
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default PartialCancelModal;
